import { useRef, useState } from "react";
import { articleApi, type ArticleUnitSummary } from "../api/articleUnits";
import { ApiError } from "../api/types";
import { Button, Switch, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { Copy, FileText, PenLine, Plus } from "../compose/Icon";
import {
  SegmentedControl,
  type SegmentedOption,
} from "@norkron/web-segmented-control";
import { briefWire, domPasteText, type BriefSource } from "../lib/briefIngest";
import {
  BRIEF_ACCEPT,
  baseName,
  fileRejectReason,
  formatFromName,
  readFileAsBase64,
  type BriefFormat,
} from "../lib/fileIngest";
import { isValidSlug, slugify } from "../lib/slug";

/**
 * New article — the create half of the article-unit surface.
 *
 * FOUR ways to hand in a brief (the owner's words: "upload the brief either in
 * docx or md format or just copy and paste the text to the editor or write it
 * directly in the editor"), all landing on ONE server transaction that creates
 * the project, its three role-tagged resources, and — when asked — the
 * `el.briefings` work item an agent polls for.
 *
 * COMPOSED, not rebuilt (RULE ZERO): `<Dialog>` + `<TextField>` + `<Switch>` +
 * `<SegmentedControl>` are the app's existing primitives; the file→base64 path
 * is `lib/fileIngest` (shared with `<UploadDialog>`); the four-modes→wire
 * lowering is `lib/briefIngest`; the slug preview is `lib/slug` (shared with
 * `<CreateWorkspaceDialog>`). This component owns only the arrangement.
 *
 * ── THE PASTE SURFACE IS A REAL contentEditable, DELIBERATELY ───────────────
 * A `<textarea>` cannot hold a heading, a bold run or a table, so pasting a
 * Word brief into one silently flattens it. This box lets the browser do its
 * NATIVE paste and reads what landed: `innerHTML` for the rich flavour, and
 * `briefIngest.domPasteText` — NOT `innerText` — for the plain one. That reader
 * separates every block boundary by a BLANK LINE because the plain flavour goes
 * out as `format: "md"`, where a single newline is a soft break: reading it with
 * one newline per boundary is what collapsed every multi-paragraph pasted brief
 * into a single paragraph. (`innerText` is layout-defined and the test DOM
 * aliases it to `textContent`, which returns no newlines at all.)
 *
 * Nothing here touches `clipboardData`. On a synthetic event it is `null`, and a
 * test that mocks a browser API into existence cannot catch the bug that ships;
 * reading the DOM the browser actually produced needs no such fiction.
 */

type CreateMode = "upload" | "paste" | "editor";

const MODE_SEGMENTS: SegmentedOption<CreateMode>[] = [
  { value: "upload", label: "Upload a file", icon: <FileText size={14} strokeWidth={1.5} /> },
  { value: "paste", label: "Paste the text", icon: <Copy size={14} strokeWidth={1.5} /> },
  { value: "editor", label: "Write it here", icon: <PenLine size={14} strokeWidth={1.5} /> },
];

/** The two brief formats the owner named. `.html` is accepted by the document
 *  library upload, but an html BRIEF has no product story here. */
const BRIEF_FORMATS: readonly BriefFormat[] = ["docx", "md"];

/** Turn a failed create into a sentence the user can act on. The server's own
 *  message wins when it sent one — it knows more than we do. */
function createErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return "Could not create the article.";
  if (err.status === 409)
    return "An article with that slug already exists here — change the slug.";
  if (err.status === 413)
    return "That brief is too large to send. Split it, or paste the text instead.";
  if (err.status === 404)
    return "You're not a member of this workspace, so you can't create an article in it.";
  return err.message || "Could not create the article.";
}

export function NewArticleDialog({
  slug: workspaceSlug,
  canWriteInEditor,
  onClose,
  onCreated,
}: {
  /** The WORKSPACE slug (`/w/:slug`) — not the article's own slug, which is
   *  local state below. */
  slug: string;
  /** Does the caller hold `edit_doc` here? "Write it here" creates an EMPTY
   *  brief and sends the user to the editor, whose save is `edit_doc`-gated —
   *  offering it to someone who cannot save is a control that looks like it
   *  worked and did not. They are steered to Paste instead. */
  canWriteInEditor: boolean;
  onClose: () => void;
  onCreated: (unit: ArticleUnitSummary, opts: { openEditor: boolean }) => void;
}) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [mode, setMode] = useState<CreateMode>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [pasteFilled, setPasteFilled] = useState(false);
  const [enqueueAgent, setEnqueueAgent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [degraded, setDegraded] = useState<{
    unit: ArticleUnitSummary;
    note: string;
  } | null>(null);
  const pasteRef = useRef<HTMLDivElement>(null);

  const effectiveSlug = slugTouched ? slug : slugify(title);
  const busy = progress !== null;

  function pickMode(next: CreateMode) {
    setError(null);
    if (next === "editor" && !canWriteInEditor) {
      setError(
        "You can hand in a brief, but writing one here needs edit access — " +
          "paste the text instead and an editor can take it from there.",
      );
      return;
    }
    setMode(next);
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    const reason = picked
      ? fileRejectReason(picked, { formats: BRIEF_FORMATS })
      : null;
    setFile(reason ? null : picked);
    setError(reason);
    if (!reason && picked && !title.trim()) setTitle(baseName(picked.name));
  }

  /** Gather what the user supplied into the pure {@link BriefSource}. Returns a
   *  message instead when the chosen mode has nothing in it yet. */
  async function collectSource(): Promise<BriefSource | string> {
    if (mode === "editor") return { mode: "editor" };
    if (mode === "paste") {
      const el = pasteRef.current;
      const text = domPasteText(el);
      if (text.trim().length === 0) {
        return "Paste the brief text, or switch to “Write it here”.";
      }
      return { mode: "paste", html: el?.innerHTML ?? "", text };
    }
    if (!file) return "Choose a .docx or .md file, or paste the text instead.";
    const format = formatFromName(file.name);
    if (!format || !BRIEF_FORMATS.includes(format)) {
      return `“${file.name}” is not a .docx or .md file.`;
    }
    setProgress("Reading the brief…");
    const contentB64 = await readFileAsBase64(file);
    return { mode: "file", filename: file.name, format, contentB64 };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("Give the article a title.");
      return;
    }
    if (!isValidSlug(effectiveSlug)) {
      setError(
        effectiveSlug.length === 0
          ? "That title has no url-safe form — type a slug yourself."
          : "Slug must be lowercase letters, numbers and hyphens.",
      );
      return;
    }
    try {
      const source = await collectSource();
      if (typeof source === "string") {
        setProgress(null);
        setError(source);
        return;
      }
      const wire = briefWire(source);
      setProgress("Creating the project…");
      // `...wire` spreads the SERVER'S OWN field names (brief_format /
      // brief_filename / brief_content_b64) straight through — no rename step,
      // because the rename step is what silently dropped every brief.
      const res = await articleApi.createArticleUnit(workspaceSlug, {
        title: title.trim(),
        slug: effectiveSlug,
        ...wire,
        enqueue_agent_work: enqueueAgent,
      });
      setProgress(null);
      // An ingest that DEGRADED still created the project — but saying nothing
      // would hand the user a brief that is a placeholder card and call it a
      // success. Hold the dialog open with the server's own note.
      if (!res.ingest_ok) {
        setDegraded({ unit: res.unit, note: res.note });
        return;
      }
      onCreated(res.unit, { openEditor: mode === "editor" });
    } catch (err) {
      setProgress(null);
      setError(createErrorMessage(err));
    }
  }

  if (degraded) {
    return (
      <Dialog
        title="The brief could not be read"
        subtitle="The project was created — its brief was stored as a placeholder."
        onClose={onClose}
        testid="new-article-dialog"
      >
        <div className="auth__error" role="alert" data-testid="na-degraded">
          {degraded.note ||
            "We couldn't read that file. Open the project and paste the brief in."}
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            data-testid="na-open-degraded"
            onClick={() => onCreated(degraded.unit, { openEditor: true })}
          >
            Open the project
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title="New article"
      subtitle="Hand in the brief — upload it, paste it, or write it here."
      onClose={onClose}
      testid="new-article-dialog"
    >
      <form onSubmit={submit}>
        {error ? (
          <div className="auth__error" role="alert" data-testid="na-error">
            {error}
          </div>
        ) : null}

        <TextField
          label="Title"
          placeholder="Q3 Casino Review"
          value={title}
          data-testid="na-title"
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
        <TextField
          label="Slug"
          hint="Used in the URL — lowercase letters, numbers and hyphens."
          placeholder="q3-casino-review"
          value={effectiveSlug}
          data-testid="na-slug"
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
        />

        <div className="field">
          <span className="field__label">The brief</span>
          <SegmentedControl
            ariaLabel="How to hand in the brief"
            value={mode}
            options={MODE_SEGMENTS}
            // The lib's per-segment testid default is the literal
            // `segmented-control__segment`, NOT root-derived — so the prefix must
            // be named explicitly to keep the eleven `na-mode-*` selectors this
            // dialog's suite already drives. Behaviour swap, zero selector churn.
            segmentTestIdPrefix="na-mode"
            onChange={pickMode}
          />
        </div>

        {mode === "upload" ? (
          <div className="field">
            <label className="field__label" htmlFor="na-file">
              Brief file
            </label>
            <input
              id="na-file"
              className="field__input"
              type="file"
              accept={BRIEF_ACCEPT}
              data-testid="na-file"
              onChange={onPickFile}
            />
            {file ? (
              <span className="field__hint" data-testid="na-filename">
                <FileText size={13} strokeWidth={1.5} /> {file.name}
              </span>
            ) : (
              <span className="field__hint">A .docx or .md brief, up to 750 KB.</span>
            )}
          </div>
        ) : null}

        {mode === "paste" ? (
          <div className="field">
            <label className="field__label" htmlFor="na-paste">
              Paste the brief
            </label>
            <div
              id="na-paste"
              ref={pasteRef}
              className="field__input na-paste"
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              aria-label="Paste the brief"
              data-testid="na-paste"
              // `pre-wrap` so a typed newline is VISIBLE — without it the box
              // renders a multi-line brief as one run-on paragraph while the
              // model underneath still holds the line breaks.
              style={{
                minHeight: 160,
                overflowY: "auto",
                textAlign: "left",
                whiteSpace: "pre-wrap",
              }}
              onInput={() =>
                setPasteFilled(domPasteText(pasteRef.current).trim().length > 0)
              }
            />
            <span className="field__hint">
              Headings, bold, links, lists and tables survive the paste. Plain text
              is read as markdown.
            </span>
          </div>
        ) : null}

        {mode === "editor" ? (
          <div className="field">
            <p className="field__hint" data-testid="na-editor-note">
              The project starts with an empty brief and opens in the editor, where
              you write it exactly as a reviewer will read it.
            </p>
          </div>
        ) : null}

        <div className="field" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Switch
            checked={enqueueAgent}
            onChange={setEnqueueAgent}
            label="Let agents pick this up"
            data-testid="na-enqueue"
          />
          <span className="field__hint" style={{ margin: 0 }}>
            Let agents pick this up — research it and draft the article. Their work
            arrives as a proposal you accept or reject.
          </span>
        </div>

        {progress ? (
          <p className="field__hint" role="status" data-testid="na-progress">
            {progress}
          </p>
        ) : null}

        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="na-submit"
            icon={<Plus size={15} strokeWidth={1.5} />}
          >
            {busy
              ? "Creating…"
              : mode === "editor"
                ? "Create and open the editor"
                : "Create article"}
          </Button>
        </div>
        {/* `pasteFilled` drives nothing visual today; it exists so the paste box's
            emptiness is observable state rather than a DOM read at submit time. */}
        <span hidden data-testid="na-paste-filled">{String(pasteFilled)}</span>
      </form>
    </Dialog>
  );
}
